"use client";

import { useLocale, useTranslations } from "next-intl";
import { FC, useMemo } from "react";
import { Image } from "react-datocms";

import classNames from "classnames";

import { getLink } from "utils";

import { Button } from "components/atoms/Button/Button";

import { PersonRecord } from "@graphql/types/graphql";
import { Locale } from "consts/locale";
import s from "./Person.module.scss";

export const Person: FC<PersonRecord & { layout: string }> = ({
  id,
  image,
  name,
  role,
  phone,
  linkedin,
  calendly,
  subpage,
  layout,
}) => {
  const t = useTranslations("common");
  const locale = useLocale() as Locale;

  const personImage = useMemo(() => {
    if (layout === "circle" && image) {
      return (
        <div className={s.PersonCircle}>
          <Image data={image.responsiveImage} className={s.PersonCircleImage} />
        </div>
      );
    }

    if (layout === "square" && image) {
      return (
        <div className={s.PersonSquare}>
          <Image data={image.responsiveImage} layout="responsive" className={s.PersonSquareImage} />
        </div>
      );
    }

    return null;
  }, [image, layout]);

  const personMeta = useMemo(() => {
    if (phone || linkedin || subpage) {
      return (
        <div className={s.Contact}>
          {phone && (
            <Button
              label={phone}
              link={{ href: `tel:${phone}` }}
              theme={layout === "circle" ? "textual" : "textual-white"}
            />
          )}
          {linkedin && (
            <Button
              label="LinkedIn"
              link={{ href: linkedin }}
              theme={layout === "circle" ? "textual" : "textual-white"}
              openLinkInNewTab
            />
          )}
          {calendly && (
            <Button
              label={t("make-an-appointment")}
              link={{ href: calendly }}
              theme={layout === "circle" ? "textual" : "textual-white"}
              openLinkInNewTab
            />
          )}
          {subpage && (
            <Button
              label={t("read-more")}
              link={getLink(subpage, locale)}
              theme={layout === "circle" ? "textual" : "textual-white"}
            />
          )}
        </div>
      );
    }

    return null;
  }, [t, layout, phone, linkedin, calendly, subpage, locale]);

  return (
    <div
      className={classNames(s.Person, {
        [s.IsCircle]: layout === "circle",
        [s.IsSquare]: layout === "square",
      })}
      key={id}
    >
      {personImage}
      <div className={s.Content}>
        <h3 className={s.Name}>{name}</h3>
        {role && <div className={s.Role}>{role}</div>}
        {personMeta}
      </div>
    </div>
  );
};
